import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Search from 'ps-components/Search/Search';
import Dropdown from 'ps-components/Dropdown/Dropdown';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import AdvancedCell from 'ps-components/AdvancedTable/AdvancedCell';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import * as dataCutMigrationChangeActionCreators from './actions/DataCutMigrationActions';
import classnames from 'classnames';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import './DataCutMigration.scss';
import isIE from 'ps-components/Utils/checkForIE';
import SlideoutSubmitSuccess from 'ps-components/Slideout/SubmitSuccess/SlideoutSubmitSuccess';
import Popover from 'ps-components/Popover/Popover';
import { Tooltip } from 'ps-design';
import { withRouter } from 'react-router-dom';

function DataCutMigration(props) {
  const [searchText, setSearchText] = useState('');
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [renderConfirmation, setRenderConfirmation] = useState(false);
  const [renderPopover, setRenderPopover] = useState(false);
  const [bulkException, setBulkException] = useState('');
  const [allSelected, setAllSelected] = useState(false);
  const [sort, setSort] = useState([]);
  const { defaultBenchmarkYear } = props;
  const newSurveyYear = parseInt(defaultBenchmarkYear) + 1;

  useEffect(() => {
    if (props.isAgingReducerLoaded) {
      props.actions.getDataCutSurveys(defaultBenchmarkYear, newSurveyYear, searchText, '', false);
    }
  }, [props.isAgingReducerLoaded]);

  function searchOnChange(event) {
    setSearchText(event?.target?.value);
  }

  function searchOnClick() {
    setSearchText('');
    props.actions.getDataCutSurveys(defaultBenchmarkYear, newSurveyYear, '', '', false);
  }

  function handleSortChange(sortValue) {
    setSort(sortValue);
    props.actions.getDataCutSurveys(
      defaultBenchmarkYear,
      newSurveyYear,
      searchText,
      sortValue[0]?.id,
      sortValue[0]?.desc
    );
  }

  function searchOnKeyDown(event) {
    if (event.key === 'Enter') {
      setSearchText(searchText);
      props.actions.getDataCutSurveys(defaultBenchmarkYear, newSurveyYear, searchText, '', false);
    }
  }

  function searchOnSearch() {
    setSearchText(searchText);
    props.actions.getDataCutSurveys(defaultBenchmarkYear, newSurveyYear, searchText, '', false);
  }

  function closeSelectedOverlay() {
    setSelectedSurveys([]);
    setAllSelected(false);
    setBulkException('');
  }

  function handlePopover(event) {
    event?.stopPropagation();
    setRenderPopover(!renderPopover);
  }

  const exceptionDecisionDescriptions = [
    {
      header: 'Preserve with current/zero weighting',
      description: `Match exceptions will be migrated to the new benchmark year
      using the same survey jobs and data cuts in the old survey, with current/zero weighting.
      Matches will be aged according to the aging factor(s) set up in your aging table.`
    },
    {
      header: 'Leave them as exceptions',
      description: 'Matches will not be migrated into the new benchmark year.'
    },
    {
      header: 'Do not migrate the survey',
      description:
        'None of the matches made to this survey in the current benchmark year will be migrated into the new benchmark year (but will remain in the previous benchmark year).'
    }
  ];

  const totalExceptionsHeader = (
    <div id="data-cut-migration__popover-total-exceptions" className="data-cut-migration__header-total-exceptions">
      <span>
        Total Exceptions
        <Tooltip
          className="migration-total-exceptions-tooltip"
          hoverText="Matches that cannot be migrated to the new survey due to data cut issues"
          id="data-cut-migration__total-exceptions-tooltip"
          position="top"
        />
      </span>
    </div>
  );

  const decisionExceptionsHeader = (
    <div className="data-cut-migration__header-exception-decision">
      <span>
        Decision for exceptions
        <div onClick={handlePopover} className="icon-reverse icon-circle">
          <i className="icon-info" />
        </div>
        <span className="data-cut-migration__popover-wrapper">
          {renderPopover && (
            <Popover
              popoverClassnames="data-cut-migration__popover-total-exceptions"
              relativeElement={document.querySelector('span#data-cut-migration__popover-total-exceptions')}
              display={true}
              arrowAlign="center"
              togglePopover={handlePopover}
            >
              {exceptionDecisionDescriptions.map(item => (
                <div className="data_cut_migration__popover-content-wrapper" key={item.header}>
                  <p className="data_cut_migration__popover-header">{item.header}</p>
                  <p className="data_cut_migration__popover-description">{item.description}</p>
                </div>
              ))}
            </Popover>
          )}
        </span>
      </span>
    </div>
  );

  const columnHeaders = [
    {
      Header: 'Current Surveys',
      width: 250,
      accessor: 'curSurveyTitle'
    },
    {
      Header: 'New Surveys',
      width: 180,
      accessor: 'newSurveyTitle'
    },
    {
      Header: `Total Matches`,
      width: 100,
      accessor: 'totalMatches'
    },
    {
      Header: () => totalExceptionsHeader,
      width: 120,
      accessor: 'dataCutOnlyExceptions'
    },
    {
      Header: () => decisionExceptionsHeader,
      width: 300,
      accessor: 'exceptionDecision'
    }
  ];

  const exceptionDropdownOptions = [
    'Preserve With Current Weighting',
    'Preserve With 0 Weighting',
    'Leave Them As Exceptions',
    'Do Not Migrate The Survey'
  ];

  const exceptionDecisions = {
    PRESERVE_WEIGHT: 'preserve_weight',
    ZERO_WEIGHT: 'zero_weight',
    LEAVE_EXCEPTION: 'leave_exception',
    NO_MIGRATE: 'no_migrate'
  };

  function getTableRows(row) {
    function handleException(exceptionDecision) {
      switch (exceptionDecision) {
        case exceptionDecisions.PRESERVE_WEIGHT:
          return exceptionDropdownOptions[0];
        case exceptionDecisions.ZERO_WEIGHT:
          return exceptionDropdownOptions[1];
        case exceptionDecisions.NO_MIGRATE:
          return exceptionDropdownOptions[3];
        case exceptionDecisions.LEAVE_EXCEPTION:
          return exceptionDropdownOptions[2];
        case exceptionDropdownOptions[0]:
          return exceptionDropdownOptions[0];
        case exceptionDropdownOptions[1]:
          return exceptionDropdownOptions[1];
        case exceptionDropdownOptions[2]:
          return exceptionDropdownOptions[2];
        case exceptionDropdownOptions[3]:
          return exceptionDropdownOptions[3];
        default:
          return exceptionDropdownOptions[0];
      }
    }

    if ((isChecked(row.original) && bulkException) || (allSelected && bulkException)) {
      row.original.exceptionDecision = bulkException;
    }

    function isChecked(survey) {
      return (
        selectedSurveys.find(
          selectedSurvey =>
            selectedSurvey.orgId === survey.orgId && selectedSurvey.curSurveyCode === survey.curSurveyCode
        ) || allSelected
      );
    }

    function onCheck(survey) {
      if (!isChecked(survey)) {
        setSelectedSurveys([...selectedSurveys, survey]);
      } else {
        const newSelection = selectedSurveys.filter(selected => selected !== survey);
        setSelectedSurveys(newSelection);
      }
    }

    const checkedClassnames = classnames('data-cut-migration__table-body-row', {
      checked: isChecked(row.original)
    });

    const exceptionsClassname = classnames({
      'data-cut-migration-matches__table-body-cell': row?.original?.totalMatches > 0,
      'data-cut-migration-exception__table-body-cell': row?.original?.dataCutOnlyExceptions > 0
    });

    const handleExceptionChange = exceptionDecision => {
      if (isChecked(row.original)) {
        setBulkException('');
      }

      if (row.id === `${row.index}`) {
        if (exceptionDecision === exceptionDropdownOptions[2]) {
          exceptionDecision = exceptionDecisions.LEAVE_EXCEPTION;
        }
        if (exceptionDecision === exceptionDropdownOptions[1]) {
          exceptionDecision = exceptionDecisions.ZERO_WEIGHT;
        }
        if (exceptionDecision === exceptionDropdownOptions[3]) {
          exceptionDecision = exceptionDecisions.NO_MIGRATE;
        }
        if (exceptionDecision === exceptionDropdownOptions[0]) {
          exceptionDecision = exceptionDecisions.PRESERVE_WEIGHT;
        }
        const migration = [
          {
            migrationId: row.original.sessionPk,
            exceptionDecision
          }
        ];
        props.actions.updateExceptionDecision(migration, migration).then(() => {
          props.actions.getDataCutSurveys(defaultBenchmarkYear, newSurveyYear, searchText, '', false);
        });
      }
    };

    return (
      <div
        className={checkedClassnames}
        key={`survey-${row?.original?.curSurveyTitle}-${row?.original?.curSurveyCode}`}
      >
        <AdvancedCell>
          <div className="data-cut-migration-checkbox__table-body-cell">
            <CheckboxInput checked={isChecked(row?.original)} onClick={() => onCheck(row.original)} />
          </div>
        </AdvancedCell>
        <AdvancedCell
          className="data-cut_migration-current__table-body-cell-wrapper"
          getCellProps={row.cells[0].getCellProps}
        >
          <div className="data-cut_migration-current__table-body-cell">
            <p className="data-cut-migration-code__table-body-cell">{row?.original?.curSurveyCode}</p>
            <p className="data-cut-migration-name__table-body-cell">{row?.original?.curSurveyTitle}</p>
          </div>
          <i className="icon-right" />
        </AdvancedCell>
        <AdvancedCell getCellProps={row.cells[1].getCellProps}>
          <p className="data-cut-migration-code__table-body-cell">{row?.original?.newSurveyCode}</p>
          <p className="data-cut-migration-name__table-body-cell">{row?.original?.newSurveyTitle}</p>
        </AdvancedCell>
        <AdvancedCell getCellProps={row.cells[2].getCellProps}>
          <p className="data-cut-migration-matches__table-body-cell">{row?.original?.totalMatches}</p>
        </AdvancedCell>
        <AdvancedCell getCellProps={row.cells[3].getCellProps}>
          <p className={exceptionsClassname}>{row?.original?.dataCutOnlyExceptions}</p>
        </AdvancedCell>
        <AdvancedCell getCellProps={row.cells[4].getCellProps}>
          <div className="data-cut-migration-decision__table-body-cell">
            <Dropdown
              className="data-cut-migration-row__dropdown-cell"
              dropdownClass="data-cut-migration-cell__dropdown"
              text={handleException(row?.original?.exceptionDecision) || exceptionDropdownOptions[0]}
              dropdownItems={exceptionDropdownOptions}
              itemOnClick={handleExceptionChange}
              leftBound={true}
              disabled={false}
            />
            <button
              className="data-cut-migration-button__table-body-cell"
              onClick={navigateToMigrationDetails.bind(this, row.original.sessionPk, props)}
            >
              Edit
            </button>
          </div>
        </AdvancedCell>
      </div>
    );
  }

  const dataCutMigrationSubmitSuccess = props.dataCutsSubmitted ? (
    <SlideoutSubmitSuccess
      className="data-cut-migration__exception-submit-success"
      submitSuccessTitle="Data cut changes submitted"
      classNameTitle="data-cut-migration__exception__title"
      submitSuccessDetails={
        'Payscale support will be notified about changes and when the surveys are ready for migration.'
      }
      classNameDetails="data-cut-migration__exception__details"
      actionText="Done"
      classNameAction="data-cut-migration__exception__action"
    />
  ) : null;

  const tableClassNames = classnames('data-cut-migration-data__table', {
    'overlay-margin': !isIE() && selectedSurveys.length > 0
  });

  const tableHeaderClassNames = classnames('data-cut-migration-table__table-header-cell', {
    hidden: selectedSurveys.length > 0
  });

  const overlayClassNames = classnames({
    'data-cut-migration__overlay': renderConfirmation
  });

  const isSubmittingBtnText = props.dataCutsSubmitted ? (
    <div>
      <i className="icon-cw" />
      Submitting...
    </div>
  ) : (
    'Submit Data Cut Changes'
  );

  function handleBulkException(bulkExceptionDecision) {
    setBulkException(bulkExceptionDecision);

    const migrations = selectedSurveys.map(survey => {
      if (bulkExceptionDecision === exceptionDropdownOptions[2]) {
        bulkExceptionDecision = exceptionDecisions.LEAVE_EXCEPTION;
      }
      if (bulkExceptionDecision === exceptionDropdownOptions[1]) {
        bulkExceptionDecision = exceptionDecisions.ZERO_WEIGHT;
      }
      if (bulkExceptionDecision === exceptionDropdownOptions[3]) {
        bulkExceptionDecision = exceptionDecisions.NO_MIGRATE;
      }
      if (bulkExceptionDecision === exceptionDropdownOptions[0]) {
        bulkExceptionDecision = exceptionDecisions.PRESERVE_WEIGHT;
      }
      return {
        migrationId: survey.sessionPk,
        exceptionDecision: bulkExceptionDecision
      };
    });
    props.actions.updateExceptionDecision(migrations, selectedSurveys).then(() => {
      props.actions.getDataCutSurveys(defaultBenchmarkYear, newSurveyYear, searchText, '', false);
    });
  }

  function navigateToMigrationDetails(migrationId, props) {
    props.actions.setLandingSearch(searchText);
    props.history.push(`/market-analysis/data-cut-migration/${migrationId}`);
  }

  const mainDropdownOptions = ['All Surveys', 'Surveys with Exceptions', 'Surveys with no Exceptions'];

  const mainDropdownText = props.dropdownFilter ? `Show ${props.dropdownFilter}` : `Show ${mainDropdownOptions[1]}`;

  const filterSurveys =
    props.dropdownFilter === mainDropdownOptions[0]
      ? props.dataCutSurveys
      : props.dropdownFilter === mainDropdownOptions[2]
      ? props.noExceptionSurveys
      : props.exceptionOnlySurveys;

  function handleMainDropdown(selectedOption) {
    props.actions.setFilterDropdown(selectedOption);
    setSelectedSurveys([]);
    setAllSelected(false);
    setBulkException('');
  }

  function handleSelectAll() {
    setAllSelected(!allSelected);
    setSelectedSurveys(filterSurveys);
  }

  const submitConfirmationDialog = (
    <div className="submit-dialog-confirmation">
      <div className="submit-dialog-confirm-top">
        <span>Submit Changes</span>
        <i onClick={() => setRenderConfirmation(false)} className="icon-cancel" />
      </div>
      <p>Are you ready to submit data cut changes?</p>
      <span className="submit-dialog-subtext">
        Once it’s submitted, PayScale customer sucess team will be notified to migrate the surveys. For any further
        changes, please re-submit them.
      </span>
      <div className="submit-dialog-confirm-bottom">
        <a onClick={() => setRenderConfirmation(false)}>Cancel</a>
        <button onClick={handleSubmit}>Yes, Submit Changes</button>
      </div>
    </div>
  );

  function handleSubmit() {
    props.actions.sendDataCutMigrationSubmissionEmail();
    setRenderConfirmation(false);
  }

  return (
    <div className="data-cut-migration__container">
      <div className={overlayClassNames} />
      <div className="data-cut-migration-search__container">
        {renderConfirmation && submitConfirmationDialog}
        {dataCutMigrationSubmitSuccess}
        <div className="data-cut-migration-search__row">
          <h3>Data Cut Changes for Migration</h3>
        </div>
        <div className="data-cut-migration-search__row-bottom">
          <Search
            className="data-cut-migration__search"
            placeholder="Search current or new surveys"
            onChange={searchOnChange}
            onClick={searchOnClick}
            onKeyDown={searchOnKeyDown}
            onSearch={searchOnSearch}
            value={searchText}
          />
          <div className="data-cut-migration-search__inner-row">
            <button
              className="pxl-btn-primary data-cut-migration__submit-btn"
              disabled={props.isSendingSubmissionEmail}
              onClick={() => setRenderConfirmation(true)}
            >
              {isSubmittingBtnText} {props.isSendingSubmissionEmail && <i className="icon-animate-spin icon-spin" />}
            </button>
          </div>
        </div>
        <div className="data-cut-migration__surveys-dropdown">
          <Dropdown
            className="data-cut-migration-search__dropdown"
            text={mainDropdownText}
            dropdownItems={mainDropdownOptions}
            disabled={false}
            itemOnClick={handleMainDropdown}
          />
        </div>
      </div>
      <div className="data-cut-migration-table__container">
        {selectedSurveys.length > 0 && (
          <div className="selection__overlay">
            <div className="selection__text">
              {`${selectedSurveys.length} selected`}
              <span className="selected-count__divider">|</span>
              <span className="add-selected-surveys__link">
                Decision for exceptions
                <div className="icon-reverse icon-circle">
                  <i className="icon-info" />
                </div>
                <Dropdown
                  className="data-cut-migration-bulk__dropdown"
                  dropdownItems={exceptionDropdownOptions}
                  text={bulkException}
                  itemOnClick={handleBulkException}
                  disabled={false}
                  staticMessage={'Changes will be applied to all selected surveys'}
                />
              </span>
            </div>
            <i className="icon-cancel" onClick={closeSelectedOverlay} />
          </div>
        )}
        <AdvancedTable
          staticTable={false}
          useSelectAll={true}
          isAllChecked={false}
          stickyHeader={true}
          useSelectAccessor={el => el.original}
          onSelectAll={handleSelectAll}
          isSelectAllDisabled={false}
          columnHeaders={columnHeaders}
          rowData={filterSurveys}
          dataLoading={props.dataCutSurveysLoading}
          getDataRow={getTableRows}
          allowSorting={true}
          useClientSideSort={false}
          onSortChange={handleSortChange}
          className={tableClassNames}
          rowClassName="data-cut-migration-table__table-row"
          headerClassName={tableHeaderClassNames}
        />
      </div>
    </div>
  );
}

DataCutMigration.propTypes = {
  history: object,
  dataCutSurveys: array,
  dataCutSurveysLoading: bool,
  totalCount: number,
  defaultBenchmarkYear: number,
  dataCutSurveysError: bool,
  dataCutExceptionsSuccess: bool,
  dataCutExceptionsSubmitting: bool,
  isAgingReducerLoaded: bool,
  dataCutsSubmitted: bool,
  exceptionOnlySurveys: array,
  noExceptionSurveys: array,
  dropdownFilter: string,
  actions: shape({
    getDataCutSurveys: func,
    resetDataCutMigrationErrors: func,
    updateExceptionDecision: func,
    sendDataCutMigrationSubmissionEmail: func,
    setFilterDropdown: func,
    setLandingSearch: func
  }),
  isSendingSubmissionEmail: bool
};

const mapStateToProps = ({ dataCutMigration, aging }) => ({
  ...dataCutMigration,
  defaultBenchmarkYear: aging.benchmarkYear,
  isAgingReducerLoaded: aging.loaded
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...dataCutMigrationChangeActionCreators
    },
    dispatch
  )
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DataCutMigration));
